<template>
  <div
    class="ccgd-card ccgd-card--borrower ccgd-card--borrower-compact"
    :class="{ selected: isSelected }"
    :data-status="statusKey"
    @click.stop="openSwiftTask(item)"
  >
    <div class="ccgd-accent" />

    <div class="ccgd-main">
      <div class="ccgd-header ccgd-header--bt">
        <div class="ccgd-title-row ccgd-title-row--bt">
          <div class="ccgd-title">
            {{ safeTitle(item) || "Borrower Task" }}
          </div>

          <div class="ccgd-right-pack">
            <div class="ccgd-meta-inline ccgd-meta-inline--assignee" v-if="assignedTo">
              <i class="fa-duotone fa-user-check ccgd-ico ccgd-ico--assignee"></i>
              <span class="ccgd-meta-text">{{ assignedTo }}</span>
            </div>

            <div class="ccgd-status-pill" :data-status="statusKey">
              <span class="ccgd-status-dot"></span>
              {{ statusText }}
            </div>
          </div>
        </div>
      </div>

      <div class="ccgd-compact-row">
        <div class="ccgd-compact-left">
          <div class="ccgd-compact-chip">
            <i class="fa-duotone fa-calendar ccgd-ico ccgd-ico--created"></i>
            <span class="ccgd-compact-label">Created</span>
            <span class="ccgd-compact-value">{{ createdDate }}</span>
          </div>

          <div class="ccgd-compact-chip" v-if="taskAgeLabel">
            <i class="fa-duotone fa-clock ccgd-ico ccgd-ico--age"></i>
            <span class="ccgd-compact-label">Age</span>
            <span class="ccgd-compact-value">{{ taskAgeLabel }}</span>
          </div>

          <div class="ccgd-compact-chip" v-if="estCloseDate">
            <i class="fa-duotone fa-flag ccgd-ico ccgd-ico--close"></i>
            <span class="ccgd-compact-label">Est</span>
            <span class="ccgd-compact-value">{{ estCloseDate }}</span>
          </div>

          <div class="ccgd-compact-chip" v-if="dueDate">
            <i class="fa-duotone fa-bell ccgd-ico ccgd-ico--due"></i>
            <span class="ccgd-compact-label">Due</span>
            <span class="ccgd-compact-value">{{ dueDate }}</span>
          </div>
        </div>

        <div class="ccgd-compact-right" @click.stop>
          <div
            class="ccgd-notify-check"
            :class="{ checked: isSelected }"
            title="Select for notification"
            @click.stop="$emit('toggle-selected', item)"
          >
            <span class="ccgd-notify-box" aria-hidden="true">
              <i v-if="isSelected" class="fa-duotone fa-check"></i>
            </span>
            <span class="ccgd-notify-text">Notify</span>
          </div>

          <slot name="actionsCell" />
        </div>
      </div>

      <div v-if="notesText !== 'N/A'" class="ccgd-notes-row">
        <div class="ccgd-desc-inline">
          <i class="fa-duotone fa-note-sticky ccgd-ico ccgd-ico--note"></i>
          <span class="ccgd-notes-block">{{ notesText }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "BorrowerTaskCard",
  props: {
    item: { type: Object, required: true },
    selectedField: { type: String, default: "selected" },
    selectedIds: { type: Set, default: () => new Set() }
  },
  computed: {
    isSelected() {
      const id = this.item?.id || this.item?.task_id
      return !!(id && this.selectedIds && this.selectedIds.has(id))
    },
    assignedTo() {
      const v = this.item && this.item.assignedto
      if (v == null) return null
      const s = String(v).trim()
      if (!s || s.toLowerCase() === "undefined" || s.toLowerCase() === "null") return null
      return s
    },
    statusText() {
      return this.item.status || "Open"
    },
    statusKey() {
      return (this.item.status || "open").toLowerCase()
    },
    createdDate() {
      const raw = this.extractCreated(this.item)
      return this.formatDate(raw)
    },
    taskAgeLabel() {
      const v = this.item.taskage != null ? `${this.item.taskage} days` : this.item.age
      return v ? String(v) : null
    },
    estCloseDate() {
      const v = this.item.estclosingdate || this.item.cust17fv
      return v ? this.formatDate(v) : null
    },
    dueDate() {
      return this.item.duedate ? this.formatDate(this.item.duedate) : null
    },
    notesText() {
      const n = this.item && (this.item.notes || this.item.description)
      if (n == null) return "N/A"
      const s = String(n).trim()
      return s ? s : "N/A"
    }
  },
  methods: {
    safeTitle(item) {
      if (!item) return ""
      const t = item.title || item.name || ""
      if (typeof t !== "string") return ""
      const looksLikeAge = /^\s*\d+\s*(day|days)\s*$/i.test(t)
      if (looksLikeAge) return item.taskname || item.tasktitle || "Task"
      return t
    },
    extractCreated(item) {
      if (!item) return null
      return item.created
    },
    clean(v) {
      if (v == null) return null
      const s = String(v).trim()
      if (!s) return null
      const low = s.toLowerCase()
      if (low === "undefined" || low === "null") return null
      return s
    },
    normalizeRow(row) {
      if (!row) return {}
      return row.dataItem && typeof row.dataItem === "object" ? row.dataItem : row
    },
    normalizeUrl(url) {
      if (!url) return null
      const s = String(url).trim()
      if (!s) return null
      if (s.startsWith("http://") || s.startsWith("https://")) return s
      return `https://${s}`
    },
    buildSwiftUrl(row) {
      const r = this.normalizeRow(row)

      const tasklink = this.normalizeUrl(r.tasklink)
      if (tasklink) return tasklink

      const id = this.clean(r.id)
      const appId = this.clean(r.appid)
      const taskId = this.clean(r.task_id) || this.clean(r.taskid)

      const base = "https://swift.goldstarfinancial.com/ConsumerPortal"

      if (!id && !appId) return null

      const resolvedId = id || appId

      if (taskId) {
        return `${base}?id=${encodeURIComponent(resolvedId)}&swiftprocessing=true&taskId=${encodeURIComponent(
          taskId
        )}&taskOpen=true`
      }

      return `${base}?id=${encodeURIComponent(resolvedId)}&swiftprocessing=true`
    },
    openSwiftTask(row) {
      const url = this.buildSwiftUrl(row)
      if (!url) return

      setTimeout(() => {
        window.open(url, "_blank", "noopener,noreferrer")
      }, 0)
    },
    formatDate(val) {
      if (!val) return "—"
      const d = new Date(val)
      if (isNaN(d.getTime())) return String(val)
      return d.toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" })
    }
  }
}
</script>

<style src="@/assets/css/commandcenter/commandcenter.css"></style>

<style scoped>
.ccgd-header--bt,
.ccgd-title-row--bt {
  width: 100%;
}

.ccgd-title-row--bt {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.ccgd-title {
  flex: 1 1 auto;
  min-width: 0;
  font-weight: 950;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ccgd-right-pack {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.ccgd-meta-inline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 900;
  color: rgba(15, 23, 42, 0.72);
  max-width: 260px;
  min-width: 0;
}

.ccgd-meta-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ccgd-status-pill {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.1);
  background: rgba(15, 23, 42, 0.03);
  font-size: 12px;
  font-weight: 950;
  color: rgba(15, 23, 42, 0.85);
  white-space: nowrap;
}

.ccgd-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: rgba(16, 185, 129, 0.9);
}

.ccgd-compact-row {
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
}

.ccgd-compact-left {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  overflow: hidden;
}

.ccgd-compact-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 9px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(15, 23, 42, 0.02);
  font-size: 12px;
  font-weight: 900;
  color: rgba(15, 23, 42, 0.75);
  flex: 0 0 auto;
  white-space: nowrap;
}

.ccgd-compact-label {
  font-size: 11px;
  font-weight: 950;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  opacity: 0.65;
}

.ccgd-compact-value {
  font-weight: 950;
  color: rgba(15, 23, 42, 0.88);
}

.ccgd-compact-right {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.ccgd-notes-row {
  margin-top: 8px;
}

.ccgd-desc-inline {
  display: inline-flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 12px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(15, 23, 42, 0.02);
  font-size: 12.5px;
  font-weight: 850;
  color: rgba(15, 23, 42, 0.72);
  width: 100%;
  max-width: 100%;
}

.ccgd-notes-block {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.ccgd-ico {
  font-size: 15px;
  opacity: 0.95;
}

.ccgd-ico--assignee {
  color: #16a34a;
}

.ccgd-ico--created {
  color: var(--notification-blue);
}

.ccgd-ico--age {
  color: #f59e0b;
}

.ccgd-ico--close {
  color: var(--notification-blue);
}

.ccgd-ico--due {
  color: #ef4444;
}

.ccgd-ico--note {
  color: var(--notification-blue);
}

/* FIX: force notify to look like checkbox again (scoped + specific) */
.ccgd-notify-check {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 36px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(15, 23, 42, 0.02);
  cursor: pointer;
  user-select: none;
  font-weight: 950;
  font-size: 12px;
  color: rgba(15, 23, 42, 0.78);
  transition: transform 0.12s ease, background 0.12s ease, border-color 0.12s ease;
}

.ccgd-notify-check:hover {
  transform: translateY(-1px);
  background: rgba(37, 99, 235, 0.06);
  border-color: rgba(37, 99, 235, 0.22);
}

.ccgd-notify-box {
  width: 18px;
  height: 18px;
  border-radius: 6px;
  border: 1.5px solid rgba(15, 23, 42, 0.25);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  transition: background 0.12s ease, border-color 0.12s ease, transform 0.12s ease;
}

.ccgd-notify-box i {
  font-size: 12px;
  opacity: 0;
  transform: scale(0.7);
  transition: opacity 0.12s ease, transform 0.12s ease;
}

.ccgd-notify-check.checked {
  background: rgba(37, 99, 235, 0.1);
  border-color: rgba(37, 99, 235, 0.35);
  color: rgba(15, 23, 42, 0.92);
}

.ccgd-notify-check.checked .ccgd-notify-box {
  background: rgba(37, 99, 235, 1);
  border-color: rgba(37, 99, 235, 1);
}

.ccgd-notify-check.checked .ccgd-notify-box i {
  opacity: 1;
  transform: scale(1);
  color: #fff;
}

.ccgd-notify-text {
  white-space: nowrap;
}

@media (max-width: 780px) {
  .ccgd-compact-row {
    flex-direction: column;
    align-items: stretch;
  }

  .ccgd-compact-left {
    flex-wrap: wrap;
  }

  .ccgd-compact-right {
    justify-content: flex-start;
  }
}
</style>
